/**
 * Confidence Indicator Component
 *
 * Visual indicator for extraction confidence levels.
 */

import { t } from '../../i18n/translations.js';

/**
 * Get confidence level classification
 * @param {number} confidence - Value between 0 and 1
 * @returns {Object} Level info with class and label
 */
export function getConfidenceLevel(confidence) {
  if (confidence >= 0.9) {
    return { level: 'high', class: 'confidence-high', label: 'High' };
  } else if (confidence >= 0.7) {
    return { level: 'medium', class: 'confidence-medium', label: 'Medium' };
  } else if (confidence >= 0.5) {
    return { level: 'low', class: 'confidence-low', label: 'Low' };
  } else {
    return { level: 'very-low', class: 'confidence-very-low', label: 'Very Low' };
  }
}

/**
 * Get color for confidence level
 * @param {number} confidence - Value between 0 and 1
 * @returns {string} CSS color variable
 */
export function getConfidenceColor(confidence) {
  const level = getConfidenceLevel(confidence);
  switch (level.level) {
    case 'high':
      return 'var(--success)';
    case 'medium':
      return 'var(--warning)';
    case 'low':
      return 'var(--error)';
    default:
      return 'var(--error)';
  }
}

/**
 * Format confidence as percentage
 * @param {number} confidence - Value between 0 and 1
 * @returns {string} Formatted percentage
 */
function formatConfidence(confidence) {
  return `${Math.round(confidence * 100)}%`;
}

/**
 * Create a confidence indicator element
 * @param {Object} options - Indicator options
 * @param {number} options.confidence - Confidence value (0-1)
 * @param {string} [options.size='medium'] - Size: 'small', 'medium', 'large'
 * @param {boolean} [options.showLabel=true] - Show text label
 * @param {boolean} [options.showPercentage=true] - Show percentage value
 * @param {boolean} [options.showBar=false] - Show progress bar
 * @returns {HTMLElement}
 */
export function createConfidenceIndicator(options = {}) {
  const {
    confidence = 0,
    size = 'medium',
    showLabel = true,
    showPercentage = true,
    showBar = false,
  } = options;

  const levelInfo = getConfidenceLevel(confidence);
  const percentage = formatConfidence(confidence);

  const container = document.createElement('div');
  container.className = `confidence-indicator confidence-${size} ${levelInfo.class}`;

  if (showBar) {
    container.innerHTML = `
      <div class="confidence-bar-container">
        <div class="confidence-bar" style="width: ${confidence * 100}%"></div>
      </div>
      ${showPercentage ? `<span class="confidence-value">${percentage}</span>` : ''}
      ${showLabel ? `<span class="confidence-label">${levelInfo.label}</span>` : ''}
    `;
  } else {
    container.innerHTML = `
      <div class="confidence-dot"></div>
      ${showPercentage ? `<span class="confidence-value">${percentage}</span>` : ''}
      ${showLabel ? `<span class="confidence-label">${levelInfo.label}</span>` : ''}
    `;
  }

  return container;
}

/**
 * Render confidence indicator as HTML string
 * @param {Object} options - Same as createConfidenceIndicator
 * @returns {string}
 */
export function renderConfidenceIndicator(options = {}) {
  const {
    confidence = 0,
    size = 'medium',
    showLabel = true,
    showPercentage = true,
    showBar = false,
  } = options;

  const levelInfo = getConfidenceLevel(confidence);
  const percentage = formatConfidence(confidence);

  if (showBar) {
    return `
      <div class="confidence-indicator confidence-${size} ${levelInfo.class}">
        <div class="confidence-bar-container">
          <div class="confidence-bar" style="width: ${confidence * 100}%"></div>
        </div>
        ${showPercentage ? `<span class="confidence-value">${percentage}</span>` : ''}
        ${showLabel ? `<span class="confidence-label">${levelInfo.label}</span>` : ''}
      </div>
    `;
  }

  return `
    <div class="confidence-indicator confidence-${size} ${levelInfo.class}">
      <div class="confidence-dot"></div>
      ${showPercentage ? `<span class="confidence-value">${percentage}</span>` : ''}
      ${showLabel ? `<span class="confidence-label">${levelInfo.label}</span>` : ''}
    </div>
  `;
}

/**
 * Create a confidence badge for inline use
 * @param {number} confidence - Confidence value (0-1)
 * @returns {string} HTML string
 */
export function renderConfidenceBadge(confidence) {
  const levelInfo = getConfidenceLevel(confidence);
  const percentage = formatConfidence(confidence);

  return `<span class="confidence-badge ${levelInfo.class}">${percentage}</span>`;
}

/**
 * Create an overall confidence summary
 * @param {Object} options - Summary options
 * @param {number} options.overallConfidence - Overall confidence (0-1)
 * @param {number} options.highConfidenceCount - Fields with high confidence
 * @param {number} options.lowConfidenceCount - Fields needing review
 * @param {number} options.totalFields - Total field count
 * @returns {HTMLElement}
 */
export function createConfidenceSummary(options = {}) {
  const {
    overallConfidence = 0,
    highConfidenceCount = 0,
    lowConfidenceCount = 0,
    totalFields = 0,
  } = options;

  const levelInfo = getConfidenceLevel(overallConfidence);
  const percentage = formatConfidence(overallConfidence);

  const container = document.createElement('div');
  container.className = 'confidence-summary';

  container.innerHTML = `
    <div class="confidence-summary-header">
      <div class="confidence-summary-title">Extraction Confidence</div>
      <div class="confidence-summary-overall ${levelInfo.class}">
        <span class="confidence-summary-value">${percentage}</span>
        <span class="confidence-summary-level">${levelInfo.label}</span>
      </div>
    </div>
    
    <div class="confidence-summary-bar">
      <div class="confidence-summary-bar-fill" style="width: ${overallConfidence * 100}%"></div>
    </div>
    
    <div class="confidence-summary-stats">
      <div class="confidence-stat">
        <span class="confidence-stat-value confidence-high">${highConfidenceCount}</span>
        <span class="confidence-stat-label">High confidence</span>
      </div>
      <div class="confidence-stat">
        <span class="confidence-stat-value confidence-low">${lowConfidenceCount}</span>
        <span class="confidence-stat-label">Needs review</span>
      </div>
      <div class="confidence-stat">
        <span class="confidence-stat-value">${totalFields}</span>
        <span class="confidence-stat-label">Total fields</span>
      </div>
    </div>
  `;

  return container;
}

export default {
  createConfidenceIndicator,
  renderConfidenceIndicator,
  renderConfidenceBadge,
  createConfidenceSummary,
  getConfidenceLevel,
  getConfidenceColor,
  formatConfidence,
};
